import Separator from "@components/CustomTypography/Separator/Separator"
import ButtonComponent from "@components/Theme/Button/Button"
import {AdvancedSideAssetModuleType} from "@hooks/WorkWithJson/GetComponentDataByKey/GetComponentDataByKeyTypes"
import {AnimatePresence, LayoutGroup, motion} from "framer-motion"
import {useEffect, useState} from "react"
import {ReactSVG} from "react-svg"

import Typography, {Size, Variant} from "../Typography/Typography"

import styles from "./FeaturesGrid.module.scss"

type FeaturesGridProps = {
	data: {
		categories: {
			name: string
			content: AdvancedSideAssetModuleType["mainContent"]
		}[]
	}
}

export const FeaturesGrid = ({data}: FeaturesGridProps) => {
	const [activeCategory, setActiveCategory] = useState(0)
	const [activeIndex, setActiveIndex] = useState<number | null>(null)
	const [hoveredIndex] = useState<number | null>(null)

	// const layoutId = "feature-popup"
	const allCategories = data.categories
	const currentItems = allCategories[activeCategory]?.content ?? []

	useEffect(() => {
		if (activeIndex === null) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowRight") {
				setActiveIndex((prev) =>
					typeof prev === "number"
						? (prev + 1) % currentItems.length
						: 0,
				)
			} else if (e.key === "ArrowLeft") {
				setActiveIndex((prev) =>
					typeof prev === "number"
						? (prev - 1 + currentItems.length) % currentItems.length
						: currentItems.length - 1,
				)
			} else if (e.key === "Escape") {
				setActiveIndex(null)
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [activeIndex, currentItems.length])

	return (
		<LayoutGroup>
			<div id={"communication"} />
			<div id={"collaboration"} />
			<div id={"project-management"} />
			<div id={"connectivity"} />
			<div id={"security-and-compliance"} />
			<div className={styles.wrapper}>
				{/* Tabs */}
				<div className={styles.tabs}>
					{allCategories.map((cat, i) => (
						<ButtonComponent
							key={i}
							buttonType={"primary"}
							contrast={i !== activeCategory}
							className={`${styles.tab}`}
							onClick={() => setActiveCategory(i)}
						>
							{cat.name}
						</ButtonComponent>
					))}
				</div>

				<Separator size={32} />
				<Separator size={16} />
				{/* Grid */}

				<div className={styles.grid}>
					<AnimatePresence mode={"wait"}>
						{allCategories?.[activeCategory]?.content?.map(
							(item, index) => (
								<motion.div
									key={`item-${activeCategory}-${index}`}
									// layoutId={`feature-popup-${activeCategory}-${index}`}
									className={styles.card}
									// layout
									// normal
									// initial={{opacity: 0, scale: 0.9}}
									// animate={{opacity: 1, scale: 1}}
									// exit={{opacity: 0, scale: 0.9}}
									// transition={{duration: 0.3}}
									// slid
									// initial={{ opacity: 0, y: 20 }}
									// animate={{ opacity: 1, y: 0 }}
									// exit={{ opacity: 0, y: -20 }}
									// transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
									// blur
									// initial={{ opacity: 0, filter: "blur(6px)" }}
									// animate={{ opacity: 1, filter: "blur(0px)" }}
									// exit={{ opacity: 0, filter: "blur(6px)" }}
									// transition={{ duration: 0.4 }}
									// Delay
									variants={{
										hidden: {opacity: 0, scale: 0.9},
										visible: (i: number) => ({
											opacity: 1,
											scale: 1,
											transition: {delay: i * 0.05},
										}),
										exit: {opacity: 0, scale: 0.9},
									}}
									custom={index}
									initial={"hidden"}
									animate={"visible"}
									exit={"exit"}
									// whileHover={{backgroundColor: "#0E0B35"}}
									// onHoverStart={() => setHoveredIndex(index)}
									// onHoverEnd={() => setHoveredIndex(null)}
									onClick={() => setActiveIndex(index)}
								>
									<ReactSVG
										className={`${styles.icon} ${
											hoveredIndex === index
												? styles.hovered
												: ""
										}`}
										src={item.preview?.src ?? ""}
									/>
									<Separator size={32} />
									<Typography
										as={"h3"}
										content={item.title}
										contrast={index === hoveredIndex}
										size={Size.M}
										variant={Variant.Title}
										noPadding
									/>
									<Separator size={32} />
									<Typography
										content={item.content}
										contrast={index === hoveredIndex}
										size={Size.XS}
										variant={Variant.Description}
										noPadding
									/>
								</motion.div>
							),
						)}
					</AnimatePresence>
				</div>

				{/* Inactive categories  */}
				<div style={{display: "none"}} aria-hidden={"true"}>
					{allCategories.map((category, catIndex) =>
						catIndex !== activeCategory
							? category.content?.map((item, index) => (
									<div
										key={`shadow-${catIndex}-${index}`}
										className={styles.card}
									>
										<ReactSVG
											className={styles.icon}
											src={item.preview?.src ?? ""}
										/>
										<Typography
											as={"h3"}
											content={item.title}
											size={Size.M}
											variant={Variant.Title}
										/>
										<Typography
											content={item.content}
											size={Size.XS}
											variant={Variant.Description}
										/>
									</div>
							  ))
							: null,
					)}
				</div>

				{/* Overlay */}
				{/* <AnimatePresence>
					{activeIndex !== null && (
						<motion.div
							animate={{opacity: 0.5}}
							className={styles.popupOverlay}
							exit={{opacity: 0}}
							initial={{opacity: 0}}
							transition={{delay: 0.1, duration: 0.2}}
							onClick={() => setActiveIndex(null)}
						/>
					)}
				</AnimatePresence> */}

				{/* Arrows + Zoomed View */}
				{/* <NavigationArrows
					activeIndex={activeIndex}
					data={currentItems}
					setActiveIndex={setActiveIndex}
				/>

				<ZoomedCard
					activeIndex={activeIndex}
					data={currentItems}
					layoutId={`feature-popup-${activeCategory}-${activeIndex}`}
					setActiveIndex={setActiveIndex}
				/> */}
			</div>
		</LayoutGroup>
	)
}

export default FeaturesGrid
